import { useQueryGetOneBot } from "@apis:bot/bot";
import { useBranding } from "@app-setup:BrandingContext";
import { BrandedLogo } from "@components:BrandedLogo";
import { Header } from "@components:Header";
import { ShineTitle } from "@components:ShineTitle";
import { SwitchPage } from "@components:SwitchPage";
import { useBotSelection } from "@hooks:useBotSelection";
import { BotSelectionPersistent } from "@modules:BotSelection/BotSelection";
import { Box, Typography } from "@mui/material";
import { FailedCard } from "@pages:app/audio/components/FailedCard";
import { ProcessingCard } from "@pages:app/audio/components/ProcessingCard";
import { ResultView } from "@pages:app/audio/components/ResultView";
import { UploadForm } from "@pages:app/audio/components/UploadForm";
import {
  TranscriptionPhase,
  useAudioPlayer,
  useTranscriptionJob,
} from "@pages:app/audio/hooks";
import { TFunction } from "i18next";
import { useTranslation } from "react-i18next";

import { AppType } from "@:constants";

/*
 * The line under the title follows the job, as on the translation page.
 */
const subtitleFor = (
  phase: TranscriptionPhase,
  description: string,
  t: TFunction,
) => {
  switch (phase) {
    case "uploading":
    case "queued":
    case "processing":
      return t("audio.subtitle.processing");
    case "completed":
      return t("audio.subtitle.completed");
    case "failed":
      return t("audio.subtitle.failed");
    default:
      return description;
  }
};

export const Audio = () => {
  const { t } = useTranslation();
  const { getPageConfig } = useBranding();
  const audioConfig = getPageConfig("audio");

  const { selectedBotId } = useBotSelection();
  const job = useTranscriptionJob(selectedBotId ?? undefined);
  const player = useAudioPlayer(job.file);

  const { data: currentBot } = useQueryGetOneBot(
    { id: selectedBotId as string },
    { enabled: !!selectedBotId },
  );
  const canSummarize =
    !!currentBot?.ai_settings?.audio &&
    !!currentBot?.ai_settings?.summarization;

  const renderStage = () => {
    switch (job.phase) {
      case "queued":
      case "processing":
        return <ProcessingCard job={job} player={player} />;
      case "failed":
        return <FailedCard job={job} player={player} />;
      case "completed":
        return (
          <ResultView
            job={job}
            player={player}
            botId={selectedBotId as string}
            canSummarize={canSummarize}
          />
        );
      default:
        return (
          <UploadForm
            job={job}
            player={player}
            canTranscribe={!!selectedBotId}
          />
        );
    }
  };

  return (
    <Box
      sx={{
        background: (theme) => theme.palette.background.default,
        minHeight: "100vh",
        padding: "12px",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          borderRadius: "30px",
          background: (theme) => theme.palette.common.white,
          flexGrow: 1,
        }}
      >
        <Header
          sx={{
            background: (theme) => theme.palette.common.white,
            borderTopLeftRadius: "30px",
            borderTopRightRadius: "30px",
          }}
          rightSide={
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
              }}
            >
              <Box sx={{ paddingX: "0.5rem" }}>
                <BotSelectionPersistent app={AppType.Audio} />
              </Box>
              <SwitchPage />
            </Box>
          }
          leftSide={
            <Box sx={{ marginLeft: "1.5rem" }}>
              <BrandedLogo />
            </Box>
          }
        />
        <Box
          sx={{
            flex: "1 1 0% !important",
            paddingTop: "100px",
          }}
        >
          {audioConfig?.title ? (
            <Typography
              sx={{
                color: (theme) => theme.palette.text.primary,
                fontWeight: "bold",
                fontSize: "32px",
                lineHeight: "32px",
                textAlign: "center",
              }}
            >
              {audioConfig.title}
            </Typography>
          ) : (
            <ShineTitle i18nKey="audio.title" />
          )}
          <Typography
            sx={{
              color: (theme) => theme.palette.text.secondary,
              marginTop: "16px",
              fontSize: "16px",
              lineHeight: "24px",
              fontWeight: "400",
              textAlign: "center",
              maxWidth: "600px",
              mx: "auto",
            }}
          >
            {subtitleFor(
              job.phase,
              audioConfig?.description || t("audio.description"),
              t,
            )}
          </Typography>
          <Box
            sx={{
              width: "100%",
              maxWidth: 1080,
              mx: "auto",
              px: { xs: 2, md: 3 },
              pt: 8,
              pb: 8,
            }}
          >
            {renderStage()}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
